import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  Plus,
  Cart3,
  Search,
  Dash,
  ChevronDown,
  BoxSeam,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import {
  getCustomerCategoryList,
  getCustomerProductList,
} from "../../services/customerServices";
import FlexibleSkeleton from "../../components/Common/FlexibleSkeleton";
import { notify } from "../../components/Common/Alerts";
import config from "../../config";
import { formatCurrencyINR } from "../../utils/function";

const ProductCatalogueView = ({
  store,
  onBack,
  setCartData,
  setShowSummary,
  cartData,
  setShowCatalogue,
}) => {
  const { t } = useTranslation();
  const { online_order, order_start_time, order_end_time } = store;
  const isClosed = online_order === 0;

  const formatTime = (time) => {
    if (!time) return "8:00 AM";
    return new Date(`1970-01-01T${time}`).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const [cart, setCart] = useState(cartData || []);
  const [categoryList, setCategoryList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const [inputValues, setInputValues] = useState({});

  const now = new Date();

  let isOutsideTime = false;

  if (store?.order_start_time && store?.order_end_time) {
    const [startH, startM, startS] = store.order_start_time
      .split(":")
      .map(Number);
    const [endH, endM, endS] = store.order_end_time.split(":").map(Number);

    const startTime = new Date();
    startTime.setHours(startH, startM, startS);

    const endTime = new Date();
    endTime.setHours(endH, endM, endS);

    isOutsideTime = now < startTime || now > endTime;
  }

  useEffect(() => {
    setCartData(cart);
  }, [cart, setCartData]);

  useEffect(() => {
    if (!cart || cart.length === 0) return;
    const initialUnits = {};
    cart.forEach((item) => {
      const product = productList.find((p) => p.id === item.id);
      const unit = product?.units?.find(
        (u) => u.variant_id === item.variant_id,
      ) || {
        variant_id: item.variant_id,
        unit_name: item.unit_name,
        selling_price: item.unit_price,
        unit_id: item.unit_id,
      };
      initialUnits[item.id] = unit;
    });
    setSelectedUnits(initialUnits);
  }, [cart, productList]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (store.id) {
      let data = {
        store_id: store?.id,
        search: "",
        page: 1,
        limit: 10,
      };

      getCustomerCategoryList(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);
          const categories = decrypted.data || [];
          const updatedList = [{ id: "all", name: "All" }, ...categories];
          setCategoryList(updatedList);
          setSelectedCategories(["all"]);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [store]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!store?.id) return;
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingPage(true); 
      }

      try {
        setLoading(true);

        const data = {
          store_id: store.id,
          category_ids: selectedCategories.includes("all")
            ? []
            : selectedCategories,
          search: debouncedSearch || "",
          page,
          limit: config.DATA_LIMIT,
        };

        const response = await getCustomerProductList(data);
        const decrypted = decryptData(response.data.data);
        const newData = decrypted.data || [];

        setProductList((prev) =>
          page === 1 ? newData : [...prev, ...newData],
        );
        setHasMore(newData.length >= config.DATA_LIMIT);
      } catch (err) {
        notify.error(Utils.getErrorMessage(err));
      } finally {
        setLoading(false);
        setLoadingPage(false);
      }
    };

    fetchProducts();
  }, [debouncedSearch, selectedCategories, page]);



    // useEffect(() => {
    //   if (selectedCustomer) {
    //     const data = {
    //       store_id: storedData?.active_store_id,
    //       customer_id: selectedCustomer?.value,
    //       search: "",
    //       page: "",
    //       limit: 50,
    //     };
  
    //     getCustomerFavProductList(data)
    //       .then((response) => {
    //         const decrypted = decryptData(response.data.data);
    //         setFavProductsList(decrypted.data);
    //       })
    //       .catch((err) => {
    //         notify.error(Utils.getErrorMessage(err));
    //       });
    //   }
    // }, [selectedCustomer]);


  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [searchQuery, selectedCategories]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (
      scrollHeight - scrollTop <= clientHeight + 50 && // near bottom
      !loading &&
      hasMore
    ) {
      setLoadingPage(true);

      setPage((prev) => prev + 1);
    }
  };

  const handleFilter = () => {
    setSearchQuery("");
    setSelectedCategories(["all"]);
    setPage(1);
    setHasMore(true);
    setProductList([]);
    setLoadingPage(false);
  };

  const updateQty = (product, qtyChange, unit, isDirect = false) => {
    setCart((prevCart) => {
      const existingIndex = prevCart.findIndex(
        (item) => item.id === product.id,
      );

      let newCart = [...prevCart];

      const unitQty = Number(unit.unit_qty || 1); // e.g. 100 gm
      const unitPrice = Number(unit.selling_price || unit.unit_price || 0);
      const pricePerUnit = unitPrice / unitQty;

      if (existingIndex >= 0) {
        const existingItem = newCart[existingIndex];

        let newQty = isDirect
          ? Number(qtyChange)
          : existingItem.qty + (qtyChange > 0 ? 1 : -1);
        // let newQty = isDirect ? Number(qtyChange) : existingItem.qty + 1;

        if (newQty <= 0) {
          newCart.splice(existingIndex, 1);
        } else {
          newCart[existingIndex] = {
            ...existingItem,
            qty: newQty,
            variant_id: unit.variant_id,
            unit_name: unit.unit_name,
            unit_price: unitPrice,
            unit_qty: unit.unit_qty,
            unit_id: unit.unit_id,
            calculated_price: Number((newQty * pricePerUnit).toFixed(2)),
          };
        }
      } else {
        if (qtyChange > 0) {
          const qty = Number(unit.unit_qty || 1);
          newCart.push({
            ...product,
            qty,
            variant_id: unit.variant_id,
            unit_name: unit.unit_name,
            unit_price: unitPrice,
            unit_qty: unit.unit_qty,
            unit_id: unit.unit_id,
            calculated_price: Number((qty * pricePerUnit).toFixed(2)),
          });
        }
      }

      return newCart;
    });
  };

  const handleQtyInputChange = (product, unit, value) => {
    setInputValues((prev) => ({
      ...prev,
      [product.id]: value,
    }));

    if (value !== "" && !isNaN(Number(value))) {
      updateQty(product, Number(value), unit, true);
    }
  };

  const handleBlur = (product, unit) => {
    const value = inputValues[product.id];

    const num = Number(value);
    const minQty = Number(unit?.unit_qty || 1);

    if (value === "" || isNaN(num) || num <= 0) {
      setCart((prev) => prev.filter((item) => item.id !== product.id));

      setInputValues((prev) => {
        const updated = { ...prev };
        delete updated[product.id];
        return updated;
      });

      return;
    }

    const finalQty = num < minQty ? minQty : num;

    updateQty(product, finalQty, unit, true);

    setInputValues((prev) => {
      const updated = { ...prev };
      delete updated[product.id];
      return updated;
    });
  };
  const totalItems = new Set(cart.map((item) => item.id)).size;

  const totalPrice = cart.reduce((sum, item) => {
    const unitBase = Number(item.unit_qty || 1);
    return sum + (item.qty / unitBase) * item.unit_price;
  }, 0);

  const handleViewCart = () => {
    setCartData(cart);
    setShowSummary(true);
  };

  const handleUnitChange = (product, newUnit) => {
    setCart((prev) =>
      prev.map((item) => {
        if (String(item.id) !== String(product.id)) return item;

        return {
          ...item,
          variant_id: newUnit.variant_id,
          unit_name: newUnit.unit_name,
          unit_qty: newUnit.unit_qty,
          unit_short_name: newUnit.short_name,
          unit_price: Number(newUnit.selling_price),
          unit_id: newUnit.unit_id,
          qty: Number(newUnit.unit_qty || 1),
        };
      }),
    );

    setInputValues((prev) => ({
      ...prev,
      [product.id]: String(newUnit.unit_qty || 1),
    }));

    setSelectedUnits((prev) => ({
      ...prev,
      [product.id]: newUnit,
    }));

    setOpenDropdownId(null);
  };

  const removeProduct = (productId) => {
    setCart((prev) => prev.filter((item) => item.id !== productId));
  };

  return (
    <div className="flex flex-col h-screen bg-[#FCFCFC] dark:bg-slate-950 font-sans overflow-hidden transition-colors duration-300">
      <header className="bg-[#E67E22] p-5 flex items-center gap-4 shadow-lg hidden md:flex shrink-0">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center text-slate-800 dark:text-white"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="text-white font-black text-xl"> {store.name}</h1>
      </header>

      <div className="relative mb-5 md:mt-4">
        <Search
          className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          size={18}
        />
        <input
          type="text"
          placeholder="Search products..."
          className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-xl py-3.5 pl-12 pr-4 text-sm outline-none focus:ring-2 focus:ring-orange-500/20 text-slate-800 dark:text-slate-100 transition-all"
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {categoryList?.length > 0 && (
        <div className="flex flex-nowrap md:flex-wrap gap-3 overflow-x-auto md:overflow-x-visible w-full px-4 md:px-6 py-2 no-scrollbar">
          {categoryList.map((cat, index) => {
            const isSelected = selectedCategories.includes(cat.id);

            return (
              <button
                key={index}
                onClick={() => {
                  if (cat.id === "all") {
                    setSelectedCategories(["all"]);
                  } else {
                    setSelectedCategories((prev) => {
                      let updated = prev.includes(cat.id)
                        ? prev.filter((id) => id !== cat.id)
                        : [...prev.filter((id) => id !== "all"), cat.id];

                      if (updated.length === 0) {
                        return ["all"];
                      }

                      return updated;
                    });
                  }
                }}
                // className={`px-6 py-2.5 rounded-2xl font-bold text-sm transition-all whitespace-nowrap ${
                //   isSelected
                //     ? "bg-[#E67E22] text-white border-[#E67E22] shadow-md shadow-orange-100 dark:shadow-none"
                //     : "bg-white dark:bg-slate-900 text-slate-400 dark:text-slate-500 border-slate-100 dark:border-slate-800"
                // }`}

                className={`flex-shrink-0 px-6 md:px-8 py-2.5 rounded-xl font-bold text-sm transition-all border ${
                  isSelected
                    ? "bg-[#E67E22] text-white border-[#E67E22] shadow-sm"
                    : "bg-white dark:bg-slate-900 text-slate-400 border-slate-200 dark:border-slate-800"
                }`}
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="px-6 py-2">
        <div
          className={`relative flex items-center h-11 overflow-hidden rounded-2xl border transition-all duration-500 shadow-sm ${
            isClosed
              ? "bg-slate-900/90 border-slate-700 text-slate-300"
              : "bg-brand-orange/10 border-brand-orange/20 text-brand-orange"
          }`}
        >
          {/* Fixed Label Badge */}
          <div
            className={`z-20 px-4 h-full flex items-center gap-2 border-r ${
              isClosed
                ? "bg-slate-800 border-slate-700"
                : "bg-brand-orange border-brand-orange/30 text-white"
            }`}
          >
            <div
              className={`w-2 h-2 rounded-full animate-pulse ${isClosed ? "bg-red-500" : "bg-white"}`}
            />
            <span className="text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
              {isClosed ? t("offline") : t("live")}
            </span>
          </div>

          <div className="flex-1 overflow-hidden relative">
            <div className="flex w-[200%] animate-marquee items-center whitespace-nowrap">
              <div className="flex items-center gap-12 px-6">
                <span className="text-xs font-bold uppercase tracking-tight">
                  {isClosed ? (
                    <>
                      {t("orderingIsCurrentlyUnavailable")} <span>•</span>{" "}
                      {t("storeOpenSoon")}
                    </>
                  ) : (
                    <>
                      {t("nowAcceptingOrders")}! <span>•</span> {t("delivery")}:{" "}
                      {formatTime(order_start_time)} -{" "}
                      {formatTime(order_end_time)}
                    </>
                  )}
                </span>
                <span className="text-[10px] opacity-40 uppercase font-medium">
                  {isClosed ? (
                    <>••• {t("checkBackLater")} •••</>
                  ) : (
                    <>••• {t("shopNowForFreshDelivery")} •••</>
                  )}
                </span>
              </div>
              {/* Identical Duplicate for seamless loop */}
              <div className="flex items-center gap-12 px-6">
                <span className="text-xs font-bold uppercase tracking-tight">
                  {isClosed ? (
                    <>
                      {t("orderingIsCurrentlyUnavailable")} <span>•</span>{" "}
                      {t("storeOpenSoon")}
                    </>
                  ) : (
                    <>
                      {t("nowAcceptingOrders")}! <span>•</span> {t("delivery")}:{" "}
                      {formatTime(order_start_time)} -{" "}
                      {formatTime(order_end_time)}
                    </>
                  )}
                </span>
                <span className="text-[10px] opacity-40 uppercase font-medium">
                  {isClosed ? (
                    <>••• {t("checkBackLater")} •••</>
                  ) : (
                    <>••• {t("shopNowForFreshDelivery")} •••</>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main
        // className={
        //   productList?.length >= 1 && productList?.length <= 3
        //     ? "min-h-0 h-auto bg-slate-50 p-4"
        //     : "flex-1 overflow-y-auto px-6 space-y-4 pb-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4"
        // }
        // onScroll={handleScroll}

        className="flex-1 overflow-y-auto p-6 scroll-smooth pb-32"
        onScroll={handleScroll}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <FlexibleSkeleton count={10} />
          ) : productList.length > 0 ? (
            productList?.map((product) => {
              // const cartItem = cart.find(
              //   (c) => c.id === product.id && c.variant_id === currentUnit.variant_id,
              // );

              // const qty = cartItem?.qty || 0;
              const isDropdownOpen = openDropdownId === product.id;

              const cartItem = cart.find((item) => item.id === product.id);

              const qty =
                inputValues[product.id] !== undefined
                  ? inputValues[product.id]
                  : cartItem?.qty || 0;

              const currentUnit = cartItem
                ? {
                    variant_id: cartItem.variant_id,
                    unit_name: cartItem.unit_name,
                    selling_price: cartItem.unit_price,
                    unit_qty: cartItem.unit_qty,
                    short_name: cartItem.unit_short_name,
                    unit_id: cartItem.unit_id,
                  }
                : selectedUnits?.[product.id] ||
                  product.units.find((u) => u.default_unit === 1) ||
                  product.units?.[0] ||
                  {};

              return (
                <>
                  <div
                    key={product.id}
                    className="bg-white dark:bg-slate-900 rounded-[1.5rem] p-3 flex flex-col shadow-sm border border-slate-50 dark:border-slate-800 transition-all active:scale-95"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-3xl flex items-center justify-center shadow-inner relative overflow-hidden shrink-0">
                        <span className="text-3xl">{product.icon || "📦"}</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-800 dark:text-slate-100 text-sm leading-tight mb-1">
                          {product.name} ({currentUnit?.unit_qty}
                          {currentUnit?.short_name})
                        </h4>

                        <div className="flex flex-col gap-1">
                          <span className="text-lg font-black text-[#E67E22] dark:text-orange-500">
                            {formatCurrencyINR(currentUnit?.selling_price)}
                          </span>

                          {product.units?.length > 1 ? (
                            <div className="relative">
                              <button
                                onClick={() =>
                                  setOpenDropdownId(
                                    isDropdownOpen ? null : product.id,
                                  )
                                }
                                className="flex items-center gap-2 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 text-[10px] font-bold px-4 py-1.5 rounded-full border-2 border-orange-100 dark:border-orange-900/30 hover:bg-orange-100 transition-all uppercase"
                              >
                                {currentUnit?.unit_name}
                                <ChevronDown
                                  size={12}
                                  className={`transition-transform ${
                                    isDropdownOpen ? "rotate-180" : ""
                                  }`}
                                />
                              </button>

                              {isDropdownOpen && (
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setOpenDropdownId(null)}
                                  />

                                  <div className="absolute left-0 mt-2 w-56 bg-white dark:bg-slate-800 border border-orange-50 dark:border-slate-700 shadow-2xl rounded-2xl z-30 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
                                    <div className="p-1.5 space-y-1">
                                      {product?.units?.map((u) => {
                                        const isSelected =
                                          currentUnit?.variant_id ===
                                          u.variant_id;
                                        return (
                                          <button
                                            key={u.variant_id}
                                            // onClick={() => {
                                            //   setSelectedUnits((prev) => ({
                                            //     ...prev,
                                            //     [product.id]: u,
                                            //   }));
                                            //   setOpenDropdownId(null);
                                            // }}

                                            onClick={() =>
                                              handleUnitChange(product, u)
                                            }
                                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                                              isSelected
                                                ? "bg-orange-500 text-white shadow-md shadow-orange-200"
                                                : "text-slate-600 dark:text-slate-300 hover:bg-orange-50 dark:hover:bg-slate-700"
                                            }`}
                                          >
                                            <div className="flex flex-col items-start">
                                              <span
                                                className={`text-[12px] font-black uppercase tracking-tight ${isSelected ? "text-white" : "text-slate-800 dark:text-slate-100"}`}
                                              >
                                                {u.unit_name}
                                              </span>
                                              <span
                                                className={`text-[10px] font-bold ${isSelected ? "text-orange-100" : "text-slate-400 dark:text-slate-500"}`}
                                              >
                                                {t("pack")}: {u.unit_qty}
                                              </span>
                                            </div>

                                            <div className="flex flex-col items-end">
                                              <span
                                                className={`text-[13px] font-black ${isSelected ? "text-white" : "text-[#E67E22] dark:text-orange-500"}`}
                                              >
                                                {formatCurrencyINR(
                                                  u.selling_price,
                                                )}
                                                {/* {Number(u.selling_price).toFixed(2)} */}
                                              </span>
                                              {isSelected && (
                                                <span className="text-[8px] font-bold uppercase text-orange-200">
                                                  {t("current")}
                                                </span>
                                              )}
                                            </div>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                              {t("per")} {currentUnit?.unit_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      {/* {cartItem || inputValues[product.id] !== undefined ? (
                        <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-2xl p-1">
                          <button
                            // onClick={() => updateQty(product, -1, currentUnit)}
                            onClick={() => {
                              updateQty(product, -1, currentUnit);

                              setInputValues((prev) => {
                                const updated = { ...prev };
                                delete updated[product.id];
                                return updated;
                              });
                            }}
                            className="w-8 h-8 flex items-center justify-center bg-white dark:bg-slate-700 rounded-xl text-[#E67E22] dark:text-orange-500"
                          >
                            <Dash size={18} />
                          </button>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={4}
                            value={qty === 0 ? "" : qty}
                            onChange={(e) => {
                              let value = e.target.value.replace(/\D/g, "");

                              if (value === "") {
                                handleQtyInputChange(product, currentUnit, "");
                                return;
                              }

                              value = value.slice(0, 4);
                              handleQtyInputChange(product, currentUnit, value);
                            }}
                            onBlur={() => handleBlur(product, currentUnit)}
                            className="text-center font-black text-slate-700 dark:text-slate-200 text-sm w-[45px] mx-1 bg-transparent focus:outline-none"
                          />
                          <button
                            // onClick={() => updateQty(product, 1, currentUnit)}
                            onClick={() => {
                              updateQty(product, 1, currentUnit);

                              setInputValues((prev) => {
                                const updated = { ...prev };
                                delete updated[product.id];
                                return updated;
                              });
                            }}
                            className="w-8 h-8 flex items-center justify-center bg-[#E67E22] rounded-xl text-white"
                          >
                            <Plus size={18} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => updateQty(product, 1, currentUnit)}
                          className="bg-[#E67E22] text-white px-6 py-2.5 rounded-2xl font-black text-xs flex items-center gap-2
                  transition-all duration-300 active:scale-95
                  disabled:bg-gray-400 dark:disabled:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
                          disabled={store.online_order === 0 || isOutsideTime}
                        >
                          <Plus size={16} /> {t("add")}
                        </button>
                      )} */}

                      {cartItem?.qty > 0 ? (
                        <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-2xl p-1">
                          {/* minus */}
                          <button
                            onClick={() => {
                              if (qty <= currentUnit.unit_qty) {
                                removeProduct(product.id);

                                setInputValues((prev) => {
                                  const updated = { ...prev };
                                  delete updated[product.id];
                                  return updated;
                                });

                                return;
                              }

                              updateQty(
                                product,
                                -currentUnit.unit_qty,
                                currentUnit,
                              );
                            }}
                            className={`flex h-8 w-8 items-center justify-center rounded-xl shadow-sm transition-transform active:scale-90 bg-white dark:bg-slate-700 text-orange-600"
                                  `}
                            // disabled={qty <= currentUnit.unit_qty}
                          >
                            <Dash size={18} />
                          </button>

                          {/* input */}
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={4}
                            value={qty === 0 ? "" : qty}
                            onChange={(e) => {
                              let value = e.target.value.replace(/\D/g, "");
                              value = value.slice(0, 4);

                              if (value === "") {
                                handleQtyInputChange(product, currentUnit, "");
                              } else {
                                handleQtyInputChange(
                                  product,
                                  currentUnit,
                                  value,
                                );
                              }
                            }}
                            onBlur={() => handleBlur(product, currentUnit)}
                            className="text-center font-black text-slate-700 dark:text-slate-200 text-sm w-[45px] mx-1 bg-transparent focus:outline-none"
                          />

                          <button
                            onClick={() => {
                              updateQty(product, 1, currentUnit);

                              setInputValues((prev) => {
                                const updated = { ...prev };
                                delete updated[product.id];
                                return updated;
                              });
                            }}
                            className="w-8 h-8 flex items-center justify-center bg-[#E67E22] rounded-xl text-white"
                          >
                            <Plus size={18} />
                          </button>
                        </div>
                      ) : (
                        <>
                          {(store.online_order === 1 || isOutsideTime) && (
                            <button
                              onClick={() => updateQty(product, 1, currentUnit)}
                              className="bg-[#E67E22] text-white px-6 py-2.5 rounded-2xl font-black text-xs flex items-center gap-2
                               transition-all duration-300 active:scale-95
                          disabled:bg-gray-400 dark:disabled:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
                              disabled={
                                store.online_order === 0 || isOutsideTime
                              }
                            >
                              <Plus size={16} /> {t("add")}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </>
              );
            })
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center  w-full text-center px-6 my-auto mt-10">
              <BoxSeam
                size={48}
                className="text-slate-300 dark:text-slate-700 mb-4"
              />

              <h3 className="text-lg font-bold text-slate-700 dark:text-white mb-2">
                {t("noProductFound")}
              </h3>

              <p className="text-sm text-slate-400 dark:text-slate-500 mb-5">
                {t("checkBackLater")}
              </p>

              <button
                onClick={handleFilter}
                className="px-8 py-3 bg-[#E67E22] text-white rounded-2xl font-black text-sm shadow-lg shadow-orange-500/20"
              >
                {t("refresh")}
              </button>
            </div>
          )}
        </div>

        {loadingPage && (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
          </div>
        )}
      </main>

      {totalItems > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-[280px] px-4 z-[1001] pointer-events-none pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          <div className="max-w-md mx-auto pointer-events-auto">
            <button
              className="w-full bg-[#E67E22] dark:bg-orange-600 text-white p-3 rounded-2xl flex items-center justify-between shadow-lg transition-all active:scale-95"
              onClick={() => handleViewCart(cart)}
            >
              <div className="flex items-center gap-3">
                {/* Smaller Icon Container */}
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                  <Cart3 size={20} />
                </div>

                <div className="text-left">
                  <p className="text-[9px] font-black uppercase opacity-80 leading-none mb-1">
                    {t("yourCart")}
                  </p>
                  <p className="font-bold text-sm leading-none">
                    {totalItems} {totalItems === 1 ? "Item" : "Items"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-[9px] font-black uppercase opacity-80 leading-none mb-1">
                    {t("total")}
                  </p>
                  <p className="text-base font-black leading-none">
                    {formatCurrencyINR(totalPrice)}
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCatalogueView;
